<script setup lang="ts">
import { useBreadcrumbSchema } from "~/composables/useBreadcrumbSchema";
import { BreadcrumbRoute } from "~/types/breadcrumb-routes";

definePageMeta({
    layout: "services-layout",
});

interface VaahPageContent {
    meta_tags: {
        title: string;
        meta: { name?: string; property?: string; content: string }[];
    };
    hero: {
        eyebrow?: string;
        heading: string;
        subheading?: string;
        subcopy?: string;
        ctaText?: string;
        cta2Text?: string;
        links?: { label: string; href: string; icon?: string }[];
    };
    orbit: {
        label?: string;
        agents: {
            name: string;
            tag: string;
            status: string;
            icon: string;
            progress: number;
            tasks: { label: string; done: boolean }[];
        }[];
    };
    showcase: {
        heading: string;
        headingAccent?: string;
        items: { title: string; subtitle?: string; visual: "codegen" | "checklist" | "scaffold" | "review" | "integrate" }[];
    };
    whyChoose: {
        heading: string;
        ctaText?: string;
        pills: { label: string; x: string; y: string; w: string }[];
    };
    canBuild: {
        heading: string;
        subcopy: string;
        ctaText?: string;
        cards: { title: string; desc: string }[];
    };
    working: {
        heading: string;
        subheading: string;
        description: string;
        ctaText?: string;
        steps: { title: string; description: string }[];
    };
    sdlcCompare: {
        heading: string;
        subheading: string;
        nowEyebrow: string;
        nowSub: string;
        oldEyebrow: string;
        oldSub: string;
        rows: { label: string; old: string; now: string }[];
    };
    video: {
        headingLine1: string;
        headingLine2: string;
        subheading: string;
        overlayText: string;
    };
    differentiators: {
        headingLine1: string;
        headingLine2: string;
        subcopy: string;
        cards: { title: string; desc: string; svgIcon?: string }[];
    };
    industries: {
        heading: string;
        subcopy: string;
        cardBgDark?: string;
        titleColorDark?: string;
        descColorDark?: string;
        cards: { title: string; description: string }[];
    };
    faq: {
        heading: string;
        subcopy: string;
        ctaText?: string;
        items: { title: string; description: string; group?: string }[];
    };
}

// Static Immutable Configurations
const PARTICLES = Object.freeze([
    { x: "8%", s: "4px", o: "0.85", d: "16s", delay: "-4s", dx: "42px" },
    { x: "18%", s: "3px", o: "0.7", d: "20s", delay: "-12s", dx: "-34px" },
    { x: "27%", s: "5px", o: "0.6", d: "24s", delay: "-20s", dx: "56px" },
    { x: "38%", s: "3px", o: "0.75", d: "15s", delay: "-8s", dx: "-46px" },
    { x: "47%", s: "4px", o: "0.65", d: "26s", delay: "-26s", dx: "36px" },
    { x: "56%", s: "3px", o: "0.8", d: "18s", delay: "-16s", dx: "-52px" },
    { x: "64%", s: "5px", o: "0.6", d: "22s", delay: "-2s", dx: "46px" },
    { x: "72%", s: "3px", o: "0.7", d: "14s", delay: "-10s", dx: "-36px" },
    { x: "81%", s: "4px", o: "0.75", d: "21s", delay: "-22s", dx: "40px" },
    { x: "88%", s: "3px", o: "0.65", d: "17s", delay: "-6s", dx: "-42px" },
    { x: "94%", s: "4px", o: "0.7", d: "19s", delay: "-14s", dx: "32px" },
    { x: "13%", s: "3px", o: "0.6", d: "23s", delay: "-18s", dx: "-48px" },
    { x: "5%", s: "3px", o: "0.6", d: "25s", delay: "-30s", dx: "38px" },
    { x: "22%", s: "4px", o: "0.7", d: "18s", delay: "-24s", dx: "-40px" },
    { x: "33%", s: "3px", o: "0.65", d: "22s", delay: "-9s", dx: "44px" },
    { x: "52%", s: "5px", o: "0.55", d: "28s", delay: "-34s", dx: "-38px" },
    { x: "60%", s: "3px", o: "0.75", d: "16s", delay: "-13s", dx: "34px" },
    { x: "68%", s: "4px", o: "0.6", d: "23s", delay: "-28s", dx: "-46px" },
    { x: "76%", s: "3px", o: "0.7", d: "19s", delay: "-5s", dx: "40px" },
    { x: "91%", s: "4px", o: "0.65", d: "20s", delay: "-19s", dx: "-36px" },
]);

/* Card backgrounds for the "Can build" section, themed via CSS variables
   (`.va-reasons` in the scoped styles) so they stay in sync with the
   my-app-dark DOM class on reload instead of JS color-mode state. */
const CAN_BUILD_CARD_BGS = Object.freeze([
    "var(--va-card-bg)",
    "var(--va-card-bg)",
    "",
    "var(--va-card-bg)",
    "var(--va-card-bg)",
    "",
]);

const url = useRequestURL();

// Non-blocking fetch with default fallbacks
const { data, error } = await useFetch<VaahPageContent>(`/content/vaah-agents-content.json`, {
    baseURL: url.origin,
    default: () => ({}) as VaahPageContent,
});

if (error.value) {
    console.error("[vaah-agents] content fetch failed:", error.value.status);
}

const content = computed(() => data.value || ({} as VaahPageContent));

/* FAQ data in the shape WrDesignFaqsSection expects (title/description/tags),
   derived from the same content JSON. Filter tags = "All" + the unique groups. */
const faqFilterTags = computed(() => [
    "All",
    ...new Set<string>(
        (content.value?.faq?.items ?? []).map((i) => i.group ?? "General")
    ),
]);
const faqFilterItems = computed(() =>
    (content.value?.faq?.items ?? []).map((i) => ({
        title: i.title,
        description: i.description,
        tags: [i.group ?? "General"],
    }))
);

/* FAQ palette in the VaahAgents brand colors (blue → indigo), so the shared
   filterable section matches this page instead of the default purple. */
const faqColors = {
    light: {
        "--text-gradient-1": "#4FA3FF",
        "--text-gradient-2": "#8B7BFF",
        "--border-accordion": "rgba(139, 123, 255, 0.25)",
        "--bg-accordion": "linear-gradient(92.4deg, #ffffff 6.22%, #eef2ff 93.78%)",
        "--faq-arc-inner": "rgba(79, 163, 255, 0.3)",
        "--faq-arc-outer": "rgba(139, 123, 255, 0.25)",
        "--faq-ring": "#6d2d9e",
    },
    dark: {
        "--text-gradient-1": "#4FA3FF",
        "--text-gradient-2": "#8B7BFF",
        "--border-accordion": "linear-gradient(91.95deg, #21276c 4.5%, #4753dc 96.91%)",
        "--bg-accordion": "linear-gradient(93.26deg, #14183f 33.61%, #2a3480 96.74%)",
        "--faq-arc-inner": "rgba(79, 163, 255, 0.3)",
        "--faq-arc-outer": "rgba(139, 123, 255, 0.25)",
        "--faq-ring": "#4753DC",
    },
};

/* ─── SEO ─── */
const breadcrumbs = useBreadcrumbSchema(BreadcrumbRoute.Products, "VaahAgents");

const description = computed(
    () =>
        content.value?.meta_tags?.meta?.find((m) => m.name === "description")?.content ||
        "VaahAgents is WebReinvent's AI agent orchestration platform - plan work into tasks, then run agents that execute prompts sequentially via any CLI, each in its own git worktree."
);

const pageTitle = computed(
    () => content.value?.meta_tags?.title || "VaahAgents - AI Agent Orchestration Platform | WebReinvent"
);

const pageMetaKeys = new Set([
    "description",
    "og:title",
    "og:description",
    "twitter:card",
    "twitter:title",
    "twitter:description",
]);

const cmsMeta = computed(() =>
    (content.value?.meta_tags?.meta || []).filter(
        (m) => !pageMetaKeys.has(m.name ?? "") && !pageMetaKeys.has(m.property ?? "")
    )
);

useHead({
    title: pageTitle,
    link: [
        { rel: "canonical", href: url.href },
    ],
    meta: [
        ...cmsMeta.value,
        { name: "description", content: description },
        { property: "og:title", content: pageTitle },
        { property: "og:description", content: description },
        { property: "og:url", content: url.href },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: pageTitle },
        { name: "twitter:description", content: description },
    ],
    script: [
        {
            type: "application/ld+json",
            innerHTML: computed(() =>
                JSON.stringify({
                    "@context": "https://schema.org",
                    "@graph": [
                        {
                            "@type": "SoftwareApplication",
                            name: "VaahAgents",
                            applicationCategory: "DeveloperApplication",
                            operatingSystem: "Web",
                            image: `${url.origin}/images/product-hero.webp`,
                            url: `${url.origin}/products/vaah-agents`,
                            description: content.value?.hero?.subcopy || "AI agent orchestration platform.",
                            provider: {
                                "@type": "Organization",
                                name: "WebReinvent",
                                url: "https://webreinvent.com",
                                sameAs: [
                                    "https://linkedin.com/company/webreinvent",
                                    "https://instagram.com/webreinvent",
                                    "https://facebook.com/webreinvent",
                                ],
                            },
                            creator: {
                                "@type": "Organization",
                                name: "WebReinvent",
                                url: "https://webreinvent.com",
                            },
                        },
                        ...(content.value?.faq?.items?.length
                            ? [
                                {
                                    "@type": "FAQPage",
                                    mainEntity: content.value.faq.items.map((item) => ({
                                        "@type": "Question",
                                        name: item.title,
                                        acceptedAnswer: {
                                            "@type": "Answer",
                                            text: (item.description || "").replace(/<[^>]*>/g, ""),
                                        },
                                    })),
                                },
                            ]
                            : []),
                        ...(breadcrumbs.value ? [breadcrumbs.value] : []),
                    ],
                })
            ),
        },
    ],
});

// UI State
const visible = ref(false);

const formPrefilledData =
    "I'm interested in VaahAgents - I'd like to know how your AI agent orchestration platform can help my team plan, build, and ship faster.";

const openModal = () => {
    visible.value = true;
};

const onDemoClicked = () => {
    if (import.meta.client) {
        const el = document.getElementById("vaah-how-it-works");
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
        el?.focus({ preventScroll: true });
    }
};

/* Page class hook handling */
const setVaahAgentsPage = (on: boolean) => {
    if (import.meta.client) {
        document.documentElement.classList.toggle("vaah-agents-page", on);
    }
};

onMounted(() => setVaahAgentsPage(true));
onActivated(() => setVaahAgentsPage(true));
onDeactivated(() => setVaahAgentsPage(false));
onBeforeUnmount(() => setVaahAgentsPage(false));
</script>

<template>
    <div class="min-h-[100dvh] px-4 lg:px-0 va-page-bg">
        <div class="max-w-screen-xl mx-auto">
            <!-- Ambient Streaks (Dark Theme Only) -->
            <div class="hidden dark:block">
                <div
                    class="vaah-agent-glow-top absolute left-1/2 top-[-94px] -translate-x-1/2 w-[min(100vw,1238px)] h-[640px] pointer-events-none" />
                <div class="vaah-agent-glow-streaks absolute inset-0 overflow-hidden pointer-events-none max-md:hidden">
                    <div class="vaah-agent-streak"
                        style="--w:312.6px;--l:calc(50% - 196px);--t:-370px;--g:35,27,255;--o:0.35;--b:10px;--op:0.38" />
                    <div class="vaah-agent-streak"
                        style="--w:50.2px;--l:calc(50% + 240px);--t:-327px;--g:63,56,255;--o:0.28;--b:20px;--op:0.84" />
                    <div class="vaah-agent-streak"
                        style="--w:146.6px;--l:calc(50% + 90px);--t:-403px;--g:161,37,243;--o:0.71;--b:20px;--op:0.74" />
                    <div class="vaah-agent-streak"
                        style="--w:32.97px;--l:calc(50% - 136px);--t:-327px;--g:161,37,243;--o:0.71;--b:20px" />
                    <div class="vaah-agent-streak"
                        style="--w:85.07px;--l:calc(50% - 300px);--t:-279px;--g:161,37,243;--o:0.01;--b:20px;--op:0.7" />
                </div>
            </div>

            <!-- Rising Particles -->
            <div class="vaah-agent-bg-live fixed inset-0 z-0 overflow-hidden pointer-events-none" aria-hidden="true">
                <div v-for="(p, i) in PARTICLES" :key="i" class="bg-particle"
                    :style="{ '--x': p.x, '--s': p.s, '--o': p.o, '--d': p.d, '--delay': p.delay, '--dx': p.dx }" />
            </div>

            <div class="relative z-10">
                <!-- Hero Section (Eager Hydration) -->
                <WrVaahAgentsHero :hero="content?.hero" :orbit="content?.orbit" @cta-clicked="openModal"
                    @demo-clicked="onDemoClicked" />

                <div class="va-content">
                    <!-- Nuxt  Auto-Imports Below the Fold -->
                    <WrVaahAgentsShowcase v-if="content?.showcase?.items?.length" :heading="content.showcase.heading"
                        :heading-accent="content.showcase.headingAccent" :items="content.showcase.items" />

                    <div id="vaah-how-it-works" tabindex="-1" class="outline-none">
                        <WrFlutterProcessSection v-if="content?.working?.steps?.length" variant="vaah"
                            :eyebrow="content.working.subheading" :heading="content.working.heading"
                            :subcopy="content.working.description" :steps="content.working.steps"
                            :cta-text="content.working.ctaText" :cta-link="null" @cta-clicked="openModal" />
                    </div>

                    <WrAiFactoryCompare v-if="content?.sdlcCompare?.rows?.length" :heading="content.sdlcCompare.heading"
                        :subheading="content.sdlcCompare.subheading" :now-eyebrow="content.sdlcCompare.nowEyebrow"
                        :now-sub="content.sdlcCompare.nowSub" :old-eyebrow="content.sdlcCompare.oldEyebrow"
                        :old-sub="content.sdlcCompare.oldSub" :rows="content.sdlcCompare.rows" />

                    <WrDesignServiceCardsSection v-if="content?.differentiators?.cards?.length"
                        :heading-line1="content.differentiators.headingLine1"
                        :heading-line2="content.differentiators.headingLine2" :subcopy="content.differentiators.subcopy"
                        :cards="content.differentiators.cards" desc-class="text-[#501D71] dark:text-[#7782FF]"
                        @cta-clicked="openModal" />

                    <WrDesignBenefitCards v-if="content?.industries?.cards?.length"
                        :heading="content.industries.heading" :subcopy="content.industries.subcopy"
                        :cards="content.industries.cards" grid-class="md:grid-cols-2 max-w-5xl"
                        :card-bg-dark="content.industries.cardBgDark"
                        :title-color-dark="content.industries.titleColorDark"
                        :desc-color-dark="content.industries.descColorDark" />

                    <WrVaahAgentsCapabilities @cta-clicked="openModal" />

                    <WrDesignReasonsSection v-if="content?.canBuild?.cards?.length" :heading="content.canBuild.heading"
                        class="va-reasons" :subcopy="content.canBuild.subcopy" :cards="content.canBuild.cards"
                        title-class="text-[#7782FF] dark:text-white" desc-class="text-[#1C2B48] dark:text-[#7782FF]"
                        :line-opacity="60" :card-bgs="CAN_BUILD_CARD_BGS"
                        :show-desktop-card-bg="false" tablet-two-col :cta-text="content.canBuild.ctaText"
                        @cta-clicked="openModal" />

                    <WrVaahAgentsWhyChoose v-if="content?.whyChoose" :heading="content.whyChoose.heading"
                        :cta-text="content.whyChoose.ctaText" :pills="content.whyChoose.pills"
                        @cta-clicked="openModal" />
                    <WrDesignFaqsSection v-if="content?.faq?.items?.length" heading="Frequently," suffix=" asked questions"
                        :subtext="content.faq.subcopy" :filter-tags="faqFilterTags" :faq-items="faqFilterItems"
                        :cta-text="content.faq.ctaText" :hide-background="true" :show-glow="true" :colors="faqColors"
                        @cta-clicked="openModal" />
                </div>
                <WrVaahAgentsStats />
                <section class="py-8 md:py-24">
                    <WrOfficialPartners title="Official Partner of"
                        append-class="bg-[image:var(--bg-official-partner)] dark:bg-[linear-gradient(118.11deg,#0D0D42_4.47%,#060B28_79.8%)]"
                        partners-append-class="va-partners-row grid grid-cols-3 justify-items-center gap-x-4 gap-y-6 md:flex md:flex-wrap" />
                </section>


                <section class="relative dark:bg-none overflow-hidden z-20">
                    <div class="container-wrapper-xl">
                        <h2 class="text-center">
                            <WrGradientText text="Trusted for excellence and efficiency"
                                append-class="text-3xl lg:text-[48px]" />
                        </h2>
                        <p class="leading-[1.8] text-lg text-[var(--foreground)] font-anvtgrade-book mb-11">
                            WebReinvent is trusted by leading organisations to enhance their
                            development and operational efficiency.
                        </p>
                        <!-- Carousel Section -->
                        <WrTestimonialCardCarousel class="mb-12" />
                        <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
                            append-class="bg-[linear-gradient(95.99deg,#4753DC_3.69%,#262B7E_96.65%)]">View All
                        </WrButton>
                    </div>
                </section>


                <WrContactFormPopup :is-open="visible" :prefilledContent="formPrefilledData" modal-bg="#14183F"
                    class="z-30" @close="visible = false" />
            </div>
        </div>

        <WrFlutterMaskSection />
    </div>
</template>

<style scoped>
.va-page-bg {
    background-repeat: no-repeat;
}

html.my-app-dark .va-page-bg {
    background-image:
        radial-gradient(420px 420px at 10% 6%, rgba(18, 219, 255, 0.1), transparent 70%),
        radial-gradient(520px 520px at 90% 42%, rgba(77, 141, 255, 0.12), transparent 70%),
        radial-gradient(380px 380px at 28% 80%, rgba(35, 27, 255, 0.1), transparent 70%),
        linear-gradient(90deg, #000000 -3.94%, rgba(6, 1, 46, 0) 51.53%, #000000 109.81%),
        linear-gradient(109.63deg, #030226 3.67%, #06143d 83.91%);
}

@media (max-width: 640px) {
    html.my-app-dark .va-page-bg {
        background-image:
            radial-gradient(420px 420px at 10% 6%, rgba(18, 219, 255, 0.1), transparent 70%),
            radial-gradient(520px 520px at 90% 42%, rgba(77, 141, 255, 0.12), transparent 70%),
            radial-gradient(380px 380px at 28% 80%, rgba(35, 27, 255, 0.1), transparent 70%),
            linear-gradient(90deg, rgba(0, 0, 0, 0.4) -3.94%, rgba(6, 1, 46, 0) 34%, rgba(6, 1, 46, 0) 66%, rgba(0, 0, 0, 0.4) 109.81%),
            linear-gradient(109.63deg, #030226 3.67%, #06143d 83.91%);
    }
}

html:not(.my-app-dark) .va-page-bg {
    background-image:
        radial-gradient(460px 460px at 10% 6%, rgba(186, 85, 254, 0.14), transparent 70%),
        radial-gradient(560px 560px at 90% 42%, rgba(148, 103, 192, 0.14), transparent 70%),
        radial-gradient(420px 420px at 28% 80%, rgba(80, 29, 113, 0.12), transparent 70%);
}

/* "Can build" section: divider lines + mobile card backgrounds, themed by the
   my-app-dark DOM class (SSR-safe) instead of JS color-mode state. */
.va-reasons {
    --reasons-line-color: #7782FF;
    --va-card-bg: linear-gradient(229.92deg, rgba(119, 130, 255, 0.14) 0.79%, rgba(31, 38, 138, 0) 50.42%);
}

html.my-app-dark .va-reasons {
    --reasons-line-color: #1C2B48;
    --va-card-bg: linear-gradient(229.92deg, rgba(11, 13, 48, 0.5) 0.79%, rgba(31, 38, 138, 0) 50.42%);
}

.vaah-agent-glow-top {
    background: radial-gradient(ellipse at center, rgba(35, 27, 255, 0.14), transparent 70%);
}

.vaah-agent-streak {
    position: absolute;
    width: var(--w);
    height: 842px;
    left: var(--l);
    top: var(--t);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0) 100%);
    backdrop-filter: blur(40px);
    opacity: var(--op, 1);
    filter: blur(var(--b));
    transform: rotate(25.32deg);
}

.bg-particle {
    position: absolute;
    bottom: -12px;
    left: var(--x);
    width: var(--s);
    height: var(--s);
    border-radius: 50%;
    background: rgba(18, 219, 255, var(--o));
    box-shadow: 0 0 10px rgba(18, 219, 255, 0.7);
    opacity: 0;
    animation: va-rise var(--d) linear infinite;
    animation-delay: var(--delay);
}

@keyframes va-rise {
    0% {
        transform: translate(0, 0);
        opacity: 0;
    }

    8% {
        opacity: var(--o);
    }

    85% {
        opacity: var(--o);
    }

    100% {
        transform: translate(var(--dx, 0), -108vh);
        opacity: 0;
    }
}

html:not(.my-app-dark) .bg-particle {
    background: rgba(148, 103, 192, var(--o));
    box-shadow: 0 0 12px rgba(148, 103, 192, 0.7);
}

:deep(.va-content > *) {
    margin-top: 64px;
    margin-bottom: 0;
}

@media (min-width: 768px) {
    :deep(.va-content > *) {
        margin-top: 96px;
    }
}

@media (min-width: 1024px) {
    :deep(.va-content > *) {
        margin-top: 128px;
    }
}

:deep(.va-content > .vaah-why-choose + *) {
    margin-top: 0;
}

@media (prefers-reduced-motion: reduce) {
    .bg-particle {
        display: none;
    }
}

@media (max-width: 767px) {
    .bg-particle:nth-child(even) {
        display: none;
    }
}
</style>

<style>
html.vaah-agents-page #mask-section {
    display: none;
}

html.vaah-agents-page .wr-button-shell.p-px>button:not(:has(img)) {
    background-image: linear-gradient(95.99deg, #6C59B5 3.69%, #261640 96.65%);
}

html.vaah-agents-page.my-app-dark .wr-button-shell.p-px>button:not(:has(img)) {
    background-image: linear-gradient(95.99deg, #586BCC 3.69%, #23216C 96.65%);
}

html.vaah-agents-page .cmp-root {
    --cmp-accent: #501d71;
    --cmp-accent-2: #7782ff;
}

html.vaah-agents-page.my-app-dark .cmp-root {
    --cmp-accent: #7782ff;
    --cmp-accent-2: #7782ff;
}

/* Benefit cards (Industries): the shared component paints its icon tiles with
   a cyan→purple gradient; on this page use the vaah-agents theme tints instead
   (solid, no gradient). */
html.vaah-agents-page .light-icon-bg {
    background: rgba(80, 29, 113, 0.18);
}

html.vaah-agents-page .dark-icon-bg {
    background: rgba(119, 130, 255, 0.12);
}

html.vaah-agents-page section[aria-label="Service cards"] .grid {
    margin-top: 48px;
}

@media (min-width: 1024px) {
    html.vaah-agents-page section[aria-label="Service cards"] .grid {
        margin-top: 64px;
    }
}

/* Official Partners: 3-per-row grid on mobile needs smaller names so the
   logo + label fit each cell without colliding with the next partner. */
@media (max-width: 767px) {
    html.vaah-agents-page .va-partners-row>a>span {
        font-size: 14px;
    }
}

/* FAQ accordion: PrimeVue gives closed item headers its own gradient
   background-image (and mutes their text color), so the section's custom
   --bg-accordion / --border-accordion read as "applied only when open". Clear
   the header's background-image and give every title the full text color in
   both states so the custom colors show open and closed. */
html.vaah-agents-page section[aria-label="Frequently asked questions"] .p-accordionheader {
    color: var(--p-text-color);
    background-image: none !important;
}
</style>